import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { patientsAPI, transportAPI, usersAPI } from '../utils/api';
import AccessibleModal from '../components/common/AccessibleModal';

const ADDRESS_OPTIONS = [
  'Domicile usager',
  'Centre de jour - Site principal',
  'Centre de jour - Site secondaire',
  'CLSC',
  'Hôpital',
  'Clinique médicale',
  'Pharmacie',
  'Autre (saisie manuelle)',
];

const MANUAL_ADDRESS_VALUE = 'Autre (saisie manuelle)';
const HOME_ADDRESS_VALUE = 'Domicile usager';

const formatDetailedAddress = ({ streetNumber, streetName, apartment, city, province, phone }, tr) => {
  const line1 = [streetNumber, streetName].filter(Boolean).join(' ').trim();
  const line2 = [apartment, city, province].filter(Boolean).join(', ').trim();
  const main = [line1, line2].filter(Boolean).join(' - ').trim();
  if (!main) return '';
  return main;
};

const buildPatientHomeAddress = (patient, tr) => {
  if (!patient) return '';
  return formatDetailedAddress({
    streetNumber: patient.street_number,
    streetName: patient.street_name,
    apartment: patient.apartment,
    city: patient.city,
    province: patient.province,
    phone: patient.phone,
  }, tr);
};

const hasAddressFields = (data) =>
  Boolean(data?.streetNumber || data?.streetName || data?.apartment || data?.city || data?.province || data?.phone);
const addressLabel = (value, tr) => {
  const labels = {
    'Domicile usager': tr('Domicile usager', 'Patient home'),
    'Centre de jour - Site principal': tr('Centre de jour - Site principal', 'Day center - Main site'),
    'Centre de jour - Site secondaire': tr('Centre de jour - Site secondaire', 'Day center - Secondary site'),
    CLSC: 'CLSC',
    'Hôpital': tr('Hôpital', 'Hospital'),
    'Clinique médicale': tr('Clinique médicale', 'Medical clinic'),
    Pharmacie: tr('Pharmacie', 'Pharmacy'),
    'Autre (saisie manuelle)': tr('Autre (saisie manuelle)', 'Other (manual entry)'),
  };
  return labels[value] || value;
};

const getAddressPreset = (value) => {
  if (!value) return '';
  if (ADDRESS_OPTIONS.includes(value)) return value;
  return MANUAL_ADDRESS_VALUE;
};

const statusBadgeClass = (status) => {
  if (status === 'completed') return 'badge badge-success';
  if (status === 'scheduled') return 'badge badge-primary';
  if (status === 'delayed') return 'badge badge-warning';
  if (status === 'cancelled' || status === 'no_show') return 'badge badge-danger';
  return 'badge';
};

const TransportModal = ({ booking, patients, staff, readOnly, onClose, onSave }) => {
  const { i18n } = useTranslation();
  const isFr = i18n.language.startsWith('fr');
  const tr = (fr, en) => (isFr ? fr : en);
  const [form, setForm] = useState({
    patientId: booking?.patient_id || '',
    direction: booking?.direction || 'pickup',
    bookingDate: booking?.booking_date ? String(booking.booking_date).split('T')[0] : '',
    pickupAddress: booking?.pickup_address || '',
    destinationAddress: booking?.destination_address || '',
    pickupTime: booking?.pickup_time || '',
    dropoffTime: booking?.dropoff_time || '',
    assignedStaffId: booking?.assigned_staff_id || '',
    notes: booking?.notes || '',
    pickupAddressPreset: getAddressPreset(booking?.pickup_address || ''),
    destinationAddressPreset: getAddressPreset(booking?.destination_address || ''),
    pickupStreetNumber: '',
    pickupStreetName: '',
    pickupApartment: '',
    pickupCity: '',
    pickupProvince: '',
    pickupPhone: booking?.pickup_phone || '',
    destinationStreetNumber: '',
    destinationStreetName: '',
    destinationApartment: '',
    destinationCity: '',
    destinationProvince: '',
    destinationPhone: booking?.destination_phone || '',
  });
  const [loading, setLoading] = useState(false);
  const selectedPatient = patients.find((p) => p.id === form.patientId) || null;

  useEffect(() => {
    if (form.pickupAddressPreset === HOME_ADDRESS_VALUE && selectedPatient) {
      setForm((f) => ({ ...f, pickupAddress: buildPatientHomeAddress(selectedPatient, tr), pickupPhone: selectedPatient.phone || '' }));
    }
  }, [form.pickupAddressPreset, selectedPatient, tr]);

  useEffect(() => {
    if (form.destinationAddressPreset === HOME_ADDRESS_VALUE && selectedPatient) {
      setForm((f) => ({ ...f, destinationAddress: buildPatientHomeAddress(selectedPatient, tr), destinationPhone: selectedPatient.phone || '' }));
    }
  }, [form.destinationAddressPreset, selectedPatient, tr]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;
    setLoading(true);
    try {
      const pickupAddress =
        form.pickupAddressPreset === HOME_ADDRESS_VALUE
          ? buildPatientHomeAddress(selectedPatient, tr)
          : (
            hasAddressFields({
              streetNumber: form.pickupStreetNumber,
              streetName: form.pickupStreetName,
              apartment: form.pickupApartment,
              city: form.pickupCity,
              province: form.pickupProvince,
              phone: form.pickupPhone,
            })
              ? formatDetailedAddress({
                streetNumber: form.pickupStreetNumber,
                streetName: form.pickupStreetName,
                apartment: form.pickupApartment,
                city: form.pickupCity,
                province: form.pickupProvince,
                phone: form.pickupPhone,
              }, tr)
              : (form.pickupAddress || '')
          );

      const destinationAddress =
        form.destinationAddressPreset === HOME_ADDRESS_VALUE
          ? buildPatientHomeAddress(selectedPatient, tr)
          : (
            hasAddressFields({
              streetNumber: form.destinationStreetNumber,
              streetName: form.destinationStreetName,
              apartment: form.destinationApartment,
              city: form.destinationCity,
              province: form.destinationProvince,
              phone: form.destinationPhone,
            })
              ? formatDetailedAddress({
                streetNumber: form.destinationStreetNumber,
                streetName: form.destinationStreetName,
                apartment: form.destinationApartment,
                city: form.destinationCity,
                province: form.destinationProvince,
                phone: form.destinationPhone,
              }, tr)
              : (form.destinationAddress || null)
          );

      if (!pickupAddress || pickupAddress.length < 3) {
        alert(tr('Adresse de départ invalide', 'Invalid pickup address'));
        setLoading(false);
        return;
      }

      const payload = {
        patientId: form.patientId,
        direction: form.direction,
        bookingDate: form.bookingDate,
        pickupAddress,
        destinationAddress,
        pickupPhone: form.pickupPhone || null,
        destinationPhone: form.destinationPhone || null,
        pickupTime: form.pickupTime || null,
        dropoffTime: form.dropoffTime || null,
        assignedStaffId: form.assignedStaffId || null,
        notes: form.notes || null,
      };
      await transportAPI.updateBooking(booking.id, {
        ...payload,
      });
      onSave();
    } catch (err) {
      alert(err?.response?.data?.message || tr('Erreur lors de la mise à jour', 'Error while updating'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AccessibleModal
      title={readOnly ? tr('Consulter trajet', 'View trip') : tr('Modifier trajet', 'Edit trip')}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>{readOnly ? tr('Fermer', 'Close') : tr('Annuler', 'Cancel')}</button>
          {!readOnly && (
            <button type="submit" form="transport-form" className="btn btn-primary" disabled={loading}>{loading ? tr('Enregistrement...', 'Saving...') : tr('Enregistrer', 'Save')}</button>
          )}
        </>
      )}
    >
        <form id="transport-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{tr('Usager', 'Patient')}</label>
                <select className="form-select" value={form.patientId} onChange={e => setForm(f => ({ ...f, patientId: e.target.value }))} disabled={readOnly} required>
                  <option value="">{tr('Sélectionner', 'Select')}</option>
                  {patients.map(p => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{tr('Direction', 'Direction')}</label>
                <select className="form-select" value={form.direction} onChange={e => setForm(f => ({ ...f, direction: e.target.value }))} disabled={readOnly}>
                  <option value="pickup">{tr('Aller', 'Outbound')}</option>
                  <option value="dropoff">{tr('Retour', 'Return')}</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{tr('Date', 'Date')}</label>
                <input type="date" className="form-input" value={form.bookingDate} onChange={e => setForm(f => ({ ...f, bookingDate: e.target.value }))} disabled={readOnly} required />
              </div>
              <div className="form-group">
                <label className="form-label">{tr('Heure pickup', 'Pickup time')}</label>
                <input type="time" className="form-input" value={form.pickupTime} onChange={e => setForm(f => ({ ...f, pickupTime: e.target.value }))} disabled={readOnly} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{tr('Adresse départ', 'Pickup address')}</label>
              <select
                className="form-select"
                value={form.pickupAddressPreset}
                onChange={e => {
                  const preset = e.target.value;
                  setForm(f => ({
                    ...f,
                    pickupAddressPreset: preset,
                    pickupAddress: preset === HOME_ADDRESS_VALUE ? buildPatientHomeAddress(selectedPatient, tr) : '',
                    pickupPhone: preset === HOME_ADDRESS_VALUE ? (selectedPatient?.phone || '') : '',
                  }));
                }}
                disabled={readOnly}
                required
              >
                <option value="">{tr('Sélectionner', 'Select')}</option>
                {ADDRESS_OPTIONS.map(addr => (
                  <option key={addr} value={addr}>{addressLabel(addr, tr)}</option>
                ))}
              </select>
              {form.pickupAddressPreset && form.pickupAddressPreset !== HOME_ADDRESS_VALUE && (
                <div className="form-row" style={{ marginTop: 8 }}>
                  <div className="form-group">
                    <label className="form-label">{tr('Num. civique', 'Street number')}</label>
                    <input className="form-input" value={form.pickupStreetNumber} onChange={e => setForm(f => ({ ...f, pickupStreetNumber: e.target.value }))} disabled={readOnly} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{tr('Nom de la rue', 'Street name')}</label>
                    <input className="form-input" value={form.pickupStreetName} onChange={e => setForm(f => ({ ...f, pickupStreetName: e.target.value }))} disabled={readOnly} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{tr('Suite/App.', 'Suite/Apt.')}</label>
                    <input className="form-input" value={form.pickupApartment} onChange={e => setForm(f => ({ ...f, pickupApartment: e.target.value }))} disabled={readOnly} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{tr('Ville', 'City')}</label>
                    <input className="form-input" value={form.pickupCity} onChange={e => setForm(f => ({ ...f, pickupCity: e.target.value }))} disabled={readOnly} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{tr('Province', 'Province')}</label>
                    <input className="form-input" value={form.pickupProvince} onChange={e => setForm(f => ({ ...f, pickupProvince: e.target.value }))} disabled={readOnly} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{tr('Téléphone', 'Phone')}</label>
                    <input className="form-input" value={form.pickupPhone} onChange={e => setForm(f => ({ ...f, pickupPhone: e.target.value }))} disabled={readOnly} required />
                  </div>
                </div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">{tr('Adresse destination', 'Destination address')}</label>
              <select
                className="form-select"
                value={form.destinationAddressPreset}
                onChange={e => {
                  const preset = e.target.value;
                  setForm(f => ({
                    ...f,
                    destinationAddressPreset: preset,
                    destinationAddress: preset === HOME_ADDRESS_VALUE ? buildPatientHomeAddress(selectedPatient, tr) : '',
                    destinationPhone: preset === HOME_ADDRESS_VALUE ? (selectedPatient?.phone || '') : '',
                  }));
                }}
                disabled={readOnly}
              >
                <option value="">{tr('Non précisée', 'Not specified')}</option>
                {ADDRESS_OPTIONS.map(addr => (
                  <option key={addr} value={addr}>{addressLabel(addr, tr)}</option>
                ))}
              </select>
              {form.destinationAddressPreset && form.destinationAddressPreset !== HOME_ADDRESS_VALUE && (
                <div className="form-row" style={{ marginTop: 8 }}>
                  <div className="form-group">
                    <label className="form-label">{tr('Num. civique', 'Street number')}</label>
                    <input className="form-input" value={form.destinationStreetNumber} onChange={e => setForm(f => ({ ...f, destinationStreetNumber: e.target.value }))} disabled={readOnly} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{tr('Nom de la rue', 'Street name')}</label>
                    <input className="form-input" value={form.destinationStreetName} onChange={e => setForm(f => ({ ...f, destinationStreetName: e.target.value }))} disabled={readOnly} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{tr('Suite/App.', 'Suite/Apt.')}</label>
                    <input className="form-input" value={form.destinationApartment} onChange={e => setForm(f => ({ ...f, destinationApartment: e.target.value }))} disabled={readOnly} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{tr('Ville', 'City')}</label>
                    <input className="form-input" value={form.destinationCity} onChange={e => setForm(f => ({ ...f, destinationCity: e.target.value }))} disabled={readOnly} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{tr('Province', 'Province')}</label>
                    <input className="form-input" value={form.destinationProvince} onChange={e => setForm(f => ({ ...f, destinationProvince: e.target.value }))} disabled={readOnly} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{tr('Téléphone', 'Phone')}</label>
                    <input className="form-input" value={form.destinationPhone} onChange={e => setForm(f => ({ ...f, destinationPhone: e.target.value }))} disabled={readOnly} />
                  </div>
                </div>
              )}
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{tr('PAB / Chauffeur', 'PAB / Driver')}</label>
                <select className="form-select" value={form.assignedStaffId} onChange={e => setForm(f => ({ ...f, assignedStaffId: e.target.value }))} disabled={readOnly}>
                  <option value="">{tr('Non assigné', 'Unassigned')}</option>
                  {staff.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{tr('Statut', 'Status')}</label>
                <input className="form-input" value={booking?.status || ''} disabled />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{tr('Notes', 'Notes')}</label>
              <input className="form-input" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} disabled={readOnly} />
            </div>
        </form>
    </AccessibleModal>
  );
};

const TransportPage = () => {
  const { i18n } = useTranslation();
  const isFr = i18n.language.startsWith('fr');
  const tr = (fr, en) => (isFr ? fr : en);
  const { user, hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [patients, setPatients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    status: '',
  });
  const [form, setForm] = useState({
    patientId: '',
    direction: 'pickup',
    bookingDate: '',
    pickupAddress: '',
    destinationAddress: '',
    pickupTime: '',
    assignedStaffId: '',
    notes: '',
    pickupAddressPreset: '',
    destinationAddressPreset: '',
    pickupStreetNumber: '',
    pickupStreetName: '',
    pickupApartment: '',
    pickupCity: '',
    pickupProvince: '',
    pickupPhone: '',
    destinationStreetNumber: '',
    destinationStreetName: '',
    destinationApartment: '',
    destinationCity: '',
    destinationProvince: '',
    destinationPhone: '',
  });
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [modalBooking, setModalBooking] = useState(null);
  const [modalReadOnly, setModalReadOnly] = useState(false);
  const [openMenuBookingId, setOpenMenuBookingId] = useState(null);

  const canCreate = hasRole('admin', 'manager', 'usager');
  const canManageBookings = hasRole('admin', 'manager');
  const canUpdateStatus = hasRole('admin', 'manager');
  const selectedPatient = patients.find((p) => p.id === form.patientId) || null;

  const load = async () => {
    setLoading(true);
    try {
      const [bookingsRes, patientsRes, staffRes] = await Promise.all([
        transportAPI.getBookings({
          dateFrom: filters.dateFrom || undefined,
          dateTo: filters.dateTo || undefined,
          status: filters.status || undefined,
        }),
        canCreate ? patientsAPI.getAll({ limit: 500 }) : Promise.resolve({ data: [] }),
        canManageBookings ? usersAPI.getAll({ role: 'staff', limit: 500 }) : Promise.resolve({ data: { users: [] } }),
      ]);
      setBookings(bookingsRes.data || []);
      setPatients(patientsRes.data || []);
      setStaff(staffRes.data?.users || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [filters.dateFrom, filters.dateTo, filters.status, canManageBookings, canCreate]);

  useEffect(() => {
    if (user?.role === 'usager' && user?.id) {
      setForm(f => ({ ...f, patientId: user.id }));
    }
  }, [user?.role, user?.id]);
  useEffect(() => {
    if (form.pickupAddressPreset === HOME_ADDRESS_VALUE && selectedPatient) {
      setForm((f) => ({ ...f, pickupAddress: buildPatientHomeAddress(selectedPatient, tr), pickupPhone: selectedPatient.phone || '' }));
    }
  }, [form.pickupAddressPreset, selectedPatient, tr]);
  useEffect(() => {
    if (form.destinationAddressPreset === HOME_ADDRESS_VALUE && selectedPatient) {
      setForm((f) => ({ ...f, destinationAddress: buildPatientHomeAddress(selectedPatient, tr), destinationPhone: selectedPatient.phone || '' }));
    }
  }, [form.destinationAddressPreset, selectedPatient, tr]);
  useEffect(() => {
    const closeMenu = () => setOpenMenuBookingId(null);
    document.addEventListener('click', closeMenu);
    return () => document.removeEventListener('click', closeMenu);
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const pickupAddress =
        form.pickupAddressPreset === HOME_ADDRESS_VALUE
          ? buildPatientHomeAddress(selectedPatient, tr)
          : (
            hasAddressFields({
              streetNumber: form.pickupStreetNumber,
              streetName: form.pickupStreetName,
              apartment: form.pickupApartment,
              city: form.pickupCity,
              province: form.pickupProvince,
              phone: form.pickupPhone,
            })
              ? formatDetailedAddress({
                streetNumber: form.pickupStreetNumber,
                streetName: form.pickupStreetName,
                apartment: form.pickupApartment,
                city: form.pickupCity,
                province: form.pickupProvince,
                phone: form.pickupPhone,
              }, tr)
              : (form.pickupAddress || '')
          );

      const destinationAddress =
        form.destinationAddressPreset === HOME_ADDRESS_VALUE
          ? buildPatientHomeAddress(selectedPatient, tr)
          : (
            hasAddressFields({
              streetNumber: form.destinationStreetNumber,
              streetName: form.destinationStreetName,
              apartment: form.destinationApartment,
              city: form.destinationCity,
              province: form.destinationProvince,
              phone: form.destinationPhone,
            })
              ? formatDetailedAddress({
                streetNumber: form.destinationStreetNumber,
                streetName: form.destinationStreetName,
                apartment: form.destinationApartment,
                city: form.destinationCity,
                province: form.destinationProvince,
                phone: form.destinationPhone,
              }, tr)
              : (form.destinationAddress || null)
          );

      if (!pickupAddress || pickupAddress.length < 3) {
        alert(tr('Adresse de départ invalide', 'Invalid pickup address'));
        return;
      }

      const payload = {
        patientId: form.patientId,
        direction: form.direction,
        bookingDate: form.bookingDate,
        pickupAddress,
        destinationAddress,
        pickupPhone: form.pickupPhone || null,
        destinationPhone: form.destinationPhone || null,
        pickupTime: form.pickupTime || null,
        assignedStaffId: form.assignedStaffId || null,
        notes: form.notes || null,
      };
      await transportAPI.createBooking({
        ...payload,
      });
      setForm({
        patientId: user?.role === 'usager' ? user.id : '',
        direction: 'pickup',
        bookingDate: '',
        pickupAddress: '',
        destinationAddress: '',
        pickupTime: '',
        assignedStaffId: '',
        notes: '',
        pickupAddressPreset: '',
        destinationAddressPreset: '',
        pickupStreetNumber: '',
        pickupStreetName: '',
        pickupApartment: '',
        pickupCity: '',
        pickupProvince: '',
        pickupPhone: '',
        destinationStreetNumber: '',
        destinationStreetName: '',
        destinationApartment: '',
        destinationCity: '',
        destinationProvince: '',
        destinationPhone: '',
      });
      await load();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || tr('Erreur lors de la planification', 'Error while scheduling'));
    }
  };

  const quickStatus = async (booking, status) => {
    let payload = { status };
    if (status === 'delayed') {
      const delay = window.prompt(tr('Minutes de retard:', 'Delay in minutes:'), '10');
      if (delay === null) return;
      payload.delayMinutes = Number(delay);
    }
    if (status === 'cancelled') {
      const reason = window.prompt(tr('Raison d’annulation:', 'Cancellation reason:'), '');
      payload.cancelReason = reason || tr('Annulé', 'Cancelled');
    }
    try {
      await transportAPI.updateBookingStatus(booking.id, payload);
      await load();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || tr('Erreur de mise à jour statut', 'Status update error'));
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>{tr('Transport', 'Transport')}</h2>
          <p className="text-muted">{bookings.length} {tr('trajets', 'trips')}</p>
        </div>
      </div>

      <div className="card card-form" style={{ marginBottom: 16 }}>
        <div className="toolbar" style={{ marginBottom: 0 }}>
          <div className="form-row" style={{ width: '100%', margin: 0 }}>
            <div className="form-group">
              <label className="form-label">{tr('Du', 'From')}</label>
              <input type="date" className="form-input" value={filters.dateFrom} onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">{tr('Au', 'To')}</label>
              <input type="date" className="form-input" value={filters.dateTo} onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">{tr('Statut', 'Status')}</label>
              <select className="form-select" value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
                <option value="">{tr('Tous', 'All')}</option>
                <option value="scheduled">{tr('Planifié', 'Scheduled')}</option>
                <option value="delayed">{tr('Retard', 'Delayed')}</option>
                <option value="completed">{tr('Complété', 'Completed')}</option>
                <option value="cancelled">{tr('Annulé', 'Cancelled')}</option>
                <option value="no_show">Absence</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {canCreate && (
        <div className="card card-form" style={{ marginBottom: 16 }}>
          <h3 style={{ marginBottom: 12 }}>{user?.role === 'usager' ? tr('Planifier mon transport', 'Schedule my transport') : tr('Planifier un transport', 'Schedule transport')}</h3>
          <form onSubmit={handleCreate}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{tr('Usager', 'Patient')} *</label>
                <select className="form-select" value={form.patientId} onChange={e => setForm(f => ({ ...f, patientId: e.target.value }))} required disabled={user?.role === 'usager'}>
                  <option value="">{tr('Sélectionner', 'Select')}</option>
                  {patients.map(p => (
                    <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{tr('Direction', 'Direction')} *</label>
                <select className="form-select" value={form.direction} onChange={e => setForm(f => ({ ...f, direction: e.target.value }))}>
                  <option value="pickup">{tr('Aller (pickup)', 'Outbound (pickup)')}</option>
                  <option value="dropoff">{tr('Retour (dropoff)', 'Return (dropoff)')}</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{tr('Date', 'Date')} *</label>
                <input type="date" className="form-input" value={form.bookingDate} onChange={e => setForm(f => ({ ...f, bookingDate: e.target.value }))} required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{tr('Adresse départ', 'Pickup address')} *</label>
                <select
                  className="form-select"
                  value={form.pickupAddressPreset}
                  onChange={e => {
                    const preset = e.target.value;
                    setForm(f => ({
                      ...f,
                      pickupAddressPreset: preset,
                      pickupAddress: preset === HOME_ADDRESS_VALUE ? buildPatientHomeAddress(selectedPatient, tr) : '',
                      pickupPhone: preset === HOME_ADDRESS_VALUE ? (selectedPatient?.phone || '') : '',
                    }));
                  }}
                  required
                >
                  <option value="">{tr('Sélectionner', 'Select')}</option>
                  {ADDRESS_OPTIONS.map(addr => (
                    <option key={addr} value={addr}>{addressLabel(addr, tr)}</option>
                  ))}
                </select>
                {form.pickupAddressPreset && form.pickupAddressPreset !== HOME_ADDRESS_VALUE && (
                  <div className="form-row" style={{ marginTop: 8 }}>
                    <div className="form-group">
                      <label className="form-label">{tr('Num. civique', 'Street number')} *</label>
                      <input className="form-input" value={form.pickupStreetNumber} onChange={e => setForm(f => ({ ...f, pickupStreetNumber: e.target.value }))} required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">{tr('Nom de la rue', 'Street name')} *</label>
                      <input className="form-input" value={form.pickupStreetName} onChange={e => setForm(f => ({ ...f, pickupStreetName: e.target.value }))} required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">{tr('Suite/App.', 'Suite/Apt.')}</label>
                      <input className="form-input" value={form.pickupApartment} onChange={e => setForm(f => ({ ...f, pickupApartment: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">{tr('Ville', 'City')} *</label>
                      <input className="form-input" value={form.pickupCity} onChange={e => setForm(f => ({ ...f, pickupCity: e.target.value }))} required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">{tr('Province', 'Province')} *</label>
                      <input className="form-input" value={form.pickupProvince} onChange={e => setForm(f => ({ ...f, pickupProvince: e.target.value }))} required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">{tr('Téléphone', 'Phone')} *</label>
                      <input className="form-input" value={form.pickupPhone} onChange={e => setForm(f => ({ ...f, pickupPhone: e.target.value }))} required />
                    </div>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">{tr('Adresse destination', 'Destination address')}</label>
                <select
                  className="form-select"
                  value={form.destinationAddressPreset}
                onChange={e => {
                  const preset = e.target.value;
                  setForm(f => ({
                    ...f,
                    destinationAddressPreset: preset,
                    destinationAddress: preset === HOME_ADDRESS_VALUE ? buildPatientHomeAddress(selectedPatient, tr) : '',
                    destinationPhone: preset === HOME_ADDRESS_VALUE ? (selectedPatient?.phone || '') : '',
                  }));
                }}
                >
                  <option value="">{tr('Non précisée', 'Not specified')}</option>
                  {ADDRESS_OPTIONS.map(addr => (
                    <option key={addr} value={addr}>{addressLabel(addr, tr)}</option>
                  ))}
                </select>
                {form.destinationAddressPreset && form.destinationAddressPreset !== HOME_ADDRESS_VALUE && (
                  <div className="form-row" style={{ marginTop: 8 }}>
                    <div className="form-group">
                      <label className="form-label">{tr('Num. civique', 'Street number')}</label>
                      <input className="form-input" value={form.destinationStreetNumber} onChange={e => setForm(f => ({ ...f, destinationStreetNumber: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">{tr('Nom de la rue', 'Street name')}</label>
                      <input className="form-input" value={form.destinationStreetName} onChange={e => setForm(f => ({ ...f, destinationStreetName: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">{tr('Suite/App.', 'Suite/Apt.')}</label>
                      <input className="form-input" value={form.destinationApartment} onChange={e => setForm(f => ({ ...f, destinationApartment: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">{tr('Ville', 'City')}</label>
                      <input className="form-input" value={form.destinationCity} onChange={e => setForm(f => ({ ...f, destinationCity: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">{tr('Province', 'Province')}</label>
                      <input className="form-input" value={form.destinationProvince} onChange={e => setForm(f => ({ ...f, destinationProvince: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">{tr('Téléphone', 'Phone')}</label>
                      <input className="form-input" value={form.destinationPhone} onChange={e => setForm(f => ({ ...f, destinationPhone: e.target.value }))} />
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{tr('Heure pickup', 'Pickup time')}</label>
                <input type="time" className="form-input" value={form.pickupTime} onChange={e => setForm(f => ({ ...f, pickupTime: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">{tr('PAB / Chauffeur', 'PAB / Driver')}</label>
                <select className="form-select" value={form.assignedStaffId} onChange={e => setForm(f => ({ ...f, assignedStaffId: e.target.value }))} disabled={!canManageBookings}>
                  <option value="">{tr('Non assigné', 'Unassigned')}</option>
                  {staff.map(s => (
                    <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{tr('Notes', 'Notes')}</label>
              <input className="form-input" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-primary" type="submit">+ {tr('Créer transport', 'Create transport')}</button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        <div className="table-wrapper">
          {loading ? (
            <div style={{ padding: 48, textAlign: 'center' }}><div className="spinner" style={{ margin: 'auto' }} /></div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>{tr('Usager', 'Patient')}</th>
                  <th>{tr('Direction', 'Direction')}</th>
                  <th>{tr('Départ', 'Departure')}</th>
                  <th>{tr('Heure départ', 'Departure time')}</th>
                  <th>{tr('Heure arrivée', 'Arrival time')}</th>
                  <th>{tr('Assigné', 'Assigned')}</th>
                  <th>{tr('Statut', 'Status')}</th>
                  <th>{tr('Actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {bookings.length === 0 && (
                  <tr><td colSpan={9} style={{ textAlign: 'center', color: 'var(--gray-400)', padding: 32 }}>{tr('Aucun trajet', 'No trip')}</td></tr>
                )}
                {bookings.map(b => (
                  <tr key={b.id}>
                    <td>{b.booking_date?.split('T')[0] || '—'}</td>
                    <td>{b.patient_first_name} {b.patient_last_name}</td>
                    <td>{b.direction === 'pickup' ? tr('Aller', 'Outbound') : tr('Retour', 'Return')}</td>
                    <td style={{ maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.pickup_address}</td>
                    <td>{b.pickup_time || '—'}</td>
                    <td>{b.dropoff_time || '—'}</td>
                    <td>{b.driver_first_name ? `${b.driver_first_name} ${b.driver_last_name}` : '—'}</td>
                    <td>
                      <span className={statusBadgeClass(b.status)}>{b.status}</span>
                      {b.status === 'delayed' && b.delay_minutes ? <span style={{ marginLeft: 6, fontSize: 12 }}>+{b.delay_minutes} min</span> : null}
                    </td>
                    <td>
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuBookingId(curr => (curr === b.id ? null : b.id));
                          }}
                          aria-label="Ouvrir le menu d'actions"
                          aria-haspopup="menu"
                          aria-expanded={openMenuBookingId === b.id}
                        >
                          ⋮
                        </button>
                        {openMenuBookingId === b.id && (
                          <div
                            role="menu"
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              position: 'absolute',
                              top: '110%',
                              right: 0,
                              minWidth: 180,
                              background: 'white',
                              border: '1px solid var(--gray-200)',
                              borderRadius: 8,
                              boxShadow: 'var(--shadow-md)',
                              zIndex: 20,
                              padding: 6,
                            }}
                          >
                            <button
                              className="btn btn-sm btn-outline"
                              style={{ width: '100%', justifyContent: 'flex-start', marginBottom: 6 }}
                              onClick={() => {
                                setModalBooking(b);
                                setModalReadOnly(true);
                                setShowBookingModal(true);
                                setOpenMenuBookingId(null);
                              }}
                            >
                              {tr('Consulter', 'View')}
                            </button>
                            {canManageBookings && (
                              <button
                                className="btn btn-sm btn-outline"
                                style={{ width: '100%', justifyContent: 'flex-start', marginBottom: 6 }}
                                onClick={() => {
                                  setModalBooking(b);
                                  setModalReadOnly(false);
                                  setShowBookingModal(true);
                                  setOpenMenuBookingId(null);
                                }}
                              >
                                {tr('Modifier', 'Edit')}
                              </button>
                            )}
                            {canUpdateStatus && (
                              <>
                                <button
                                  className="btn btn-sm btn-outline"
                                  style={{ width: '100%', justifyContent: 'flex-start', marginBottom: 6 }}
                                  onClick={() => { setOpenMenuBookingId(null); quickStatus(b, 'completed'); }}
                                >
                                  {tr('Marquer complété', 'Mark completed')}
                                </button>
                                <button
                                  className="btn btn-sm btn-outline"
                                  style={{ width: '100%', justifyContent: 'flex-start', marginBottom: 6 }}
                                  onClick={() => { setOpenMenuBookingId(null); quickStatus(b, 'delayed'); }}
                                >
                                  {tr('Marquer retard', 'Mark delayed')}
                                </button>
                              </>
                            )}
                            {canManageBookings && (
                              <button
                                className="btn btn-sm btn-danger"
                                style={{ width: '100%', justifyContent: 'flex-start' }}
                                onClick={() => { setOpenMenuBookingId(null); quickStatus(b, 'cancelled'); }}
                              >
                                {tr('Supprimer', 'Delete')}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showBookingModal && modalBooking && (
        <TransportModal
          booking={modalBooking}
          patients={patients}
          staff={staff}
          readOnly={modalReadOnly}
          onClose={() => setShowBookingModal(false)}
          onSave={() => { setShowBookingModal(false); load(); }}
        />
      )}
    </div>
  );
};

export default TransportPage;
